import { useCallback, useMemo, useRef, useState } from 'react';
import type { BillingFormFields } from '@justifi/webcomponents/dist/loader';
import { useTranslation } from '@frontend/i18n';
import { useLatencyBustPoll } from '@frontend/payments-hooks';
import {
  justifiTokenizePayloadToPaymentMethod,
  TokenizePaymentMethodErrorCallbackPayloads,
  TokenizePaymentMethodSubmitSuccessEvent,
  UseJustifiTokenizePaymentMethod,
  useJustifiTokenizePaymentMethod,
} from '@frontend/payments-justifi-wrapper';
import { BaseFieldProps, contextFactory, useFormField } from '@frontend/design-system';
import { usePlatformComponentContext } from '../../../contexts';
import { usePaymentsPlatformTrackingIds } from '../../../utils/use-payments-platform-tracking-ids';
import { CardOnFileBaseContext, CardOnFileProviderProps } from '../context/card-on-file.context';
import { useCardOnFileJustifiApi } from '../hooks/use-card-on-file-justifi-api';

export type CardOnFileJustifiProviderProps = CardOnFileProviderProps;

export type CardOnFileJustifiContextValue = CardOnFileBaseContext & {
  fillBillingForm: (data: BillingFormFields) => Promise<void>;
  justifiProps: UseJustifiTokenizePaymentMethod['justifiProps'];
  elementRef: UseJustifiTokenizePaymentMethod['elementRef'];
  nameFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
};

export const [CardOnFileJustifiContext, useCardOnFileJustifiContext] = contextFactory<CardOnFileJustifiContextValue>(
  '',
  {} as CardOnFileJustifiContextValue
);

export const CardOnFileJustifiProvider = ({
  onCardSaved,
  onError,
  personId,
  onCancel,
  children,
}: CardOnFileJustifiProviderProps) => {
  const { locationId, isPublic } = usePlatformComponentContext();
  const { t } = useTranslation('payments');

  const cancelTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const api = useCardOnFileJustifiApi({ isPublic });

  const nameFieldProps = useFormField({ type: 'text', required: true });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: setupLink } = api.useGetPaymentMethodSetupLink({
    locationId,
  });

  const { data: paymentMethodsData, refetch: refetchPaymentMethods } = api.useGetPaymentMethodsByPerson({
    locationId,
    patientId: personId ?? '',
  });

  const paymentMethods = paymentMethodsData || [];

  const { getPollPromise } = useLatencyBustPoll({
    initialTimeoutIncrease: 100,
    queryMethod: async () => {
      const { data } = await refetchPaymentMethods();
      return data;
    },
    onPollEnd: () => {
      setIsSubmitting(false);
    },
    accessor: (data) => {
      return data?.length;
    },
  });

  const onSubmitCallback = useCallback(
    async (data: TokenizePaymentMethodSubmitSuccessEvent) => {
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
        cancelTimeoutRef.current = null;
      }
      setIsSubmitting(true);
      const paymentMethod = justifiTokenizePayloadToPaymentMethod(data);

      if (!setupLink) {
        return onError?.(t('Setup link is not available.'));
      }
      try {
        await api.savePaymentMethodBySetupLink(setupLink, {
          data: {
            personId,
            locationId,
            paymentMethodId: paymentMethod.id,
          },
        });

        await getPollPromise(paymentMethods);

        onCardSaved(paymentMethod);
      } catch (error) {
        console.error('Error saving payment method:', error);
        onError?.(t('An error occurred while saving the payment method.'));
      }
    },
    [onCardSaved, personId, setupLink, onError, api, locationId]
  );

  const onErrorCallback = useCallback(
    (data: TokenizePaymentMethodErrorCallbackPayloads) => {
      if (data.message) {
        return onError?.(data.message);
      }

      return onError?.(t('An error ocurred while saving the payment method.'));
    },
    [onError]
  );

  const { justifiProps, elementRef, fillBillingForm, tokenizePaymentMethod } = useJustifiTokenizePaymentMethod({
    locationId,
    justifiProps: {
      disableBankAccount: true, // only show card option
      hideCardBillingForm: true, // no billing form, only zip
    },
    onSubmit: onSubmitCallback,
    onError: onErrorCallback,
  });

  const onClickSubmit = useCallback(async () => {
    setIsSubmitting(true);
    await tokenizePaymentMethod();
    cancelTimeoutRef.current = setTimeout(() => {
      setIsSubmitting(false);
    }, 1000);
  }, [tokenizePaymentMethod, setIsSubmitting]);

  const { primaryBtn, secondaryBtn } = usePaymentsPlatformTrackingIds('card-on-file', {
    primaryBtn: 'save-card',
    secondaryBtn: 'cancel',
  });

  const value = useMemo<CardOnFileJustifiContextValue>(() => {
    return {
      fillBillingForm,
      onSubmit: onClickSubmit,
      nameFieldProps,
      primary: {
        disabled: isSubmitting || nameFieldProps['aria-invalid'] || !nameFieldProps.value,
        trackingId: primaryBtn,
        onClick: onClickSubmit,
      },
      secondary: {
        trackingId: secondaryBtn,
        onClick: onCancel,
      },
      shouldShowFooter: true,
      shouldShowLoading: isSubmitting || !justifiProps.accountId,
      elementRef,
      justifiProps,
    };
  }, [onClickSubmit, onCancel, fillBillingForm, isSubmitting, justifiProps, nameFieldProps]);

  return <CardOnFileJustifiContext.Provider value={value}>{children}</CardOnFileJustifiContext.Provider>;
};
